/*global google*/

import * as React from "react";
import * as PropTypes from "prop-types";
import { Api } from "./api";
import { ControlPosition, ZoomControlStyle, getControlPosition, getZoomControlStyle } from "./util";
import { Location, locationPropType, mapLocation } from "./location";

interface ZoomControlOptions {
    position?: ControlPosition;
    style?: ZoomControlStyle;
}

interface StreetViewControlOptions {
    position?: ControlPosition;
}

export interface MapComponentProps {
    map: google.maps.Map;
}

interface MapProps extends React.HTMLAttributes<HTMLDivElement> {
    children?: (map: google.maps.Map) => React.ReactNode;
    options: Replace<google.maps.MapOptions, {
        center?: google.maps.LatLng | google.maps.LatLngLiteral | Location;
        zoomControlOptions?: ZoomControlOptions;
        streetViewControlOptions?: StreetViewControlOptions;
    }>
}

interface MapState {
    map: google.maps.Map;
}

export class Map extends React.Component<MapProps, MapState> {

    // We'll set this later, once the GMaps script has loaded.
    static propTypes: PropTypes.ValidationMap<any>;

    static displayName = "GoogleMaps.Map";

    private mapRef = React.createRef<HTMLDivElement>();
    private unmounted?: boolean;

    componentDidMount() {
        if (!Api) {
            throw new Error("No GoogleMaps.Api script loader instance found!");
        }
        Api.loaded.then(() => this.init());
    }

    componentWillUnmount() {
        this.state && google.maps.event.clearInstanceListeners(this.state.map);
        this.unmounted = true;
    }

    init() {

        if (this.unmounted) return;

        Map.propTypes = {
            options: PropTypes.shape({
                center: PropTypes.oneOfType([
                    PropTypes.instanceOf(google.maps.LatLng),
                    PropTypes.shape({ lat: PropTypes.number.isRequired, lng: PropTypes.number.isRequired }),
                    locationPropType
                ]),
                zoom: PropTypes.number
            })
        }

        let map = new google.maps.Map(this.mapRef.current, this.getOptions());

        // Bounds changed will fire when we get our bounds the first time
        // We'll use that to consider our map ready for our children.
        google.maps.event.addListenerOnce(map, 'bounds_changed', () => this.setState({ map }));
    }

    componentDidUpdate(prevProps: MapProps) {
        if (this.state) {
            const {
                options: prevOptions,
            } = prevProps;

            const {
                options
            } = this.props;

            const {
                map
            } = this.state;

            if (options.center !== prevOptions.center) {
                if (options.center) {
                    map.setCenter(mapLocation(options.center));
                    google.maps.event.trigger(map, "center_set");
                }
            }

            if (options.styles !== prevOptions.styles) {
                map.setOptions({ styles: options.styles });
            }
        }
    }

    render() {
        const {
            children,
            options,
            ...divProps
        } = this.props;

        return (
            <>
                <div ref={this.mapRef} {...divProps} >
                </div>
                {this.state && this.props.children && this.props.children(this.state.map)}
            </>
        )
    }

    private getOptions(): google.maps.MapOptions {
        const {
            options: {
                center,
                zoomControlOptions,
                streetViewControlOptions,
                ...options
            }
        } = this.props;

        return {
            zoomControlOptions: zoomControlOptions && { position: getControlPosition(zoomControlOptions.position), style: getZoomControlStyle(zoomControlOptions.style) },
            streetViewControlOptions: streetViewControlOptions && { position: getControlPosition(streetViewControlOptions.position) },
            center: center && mapLocation(center),
            ...options
        };
    }
}
